import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Old Components (for backward compatibility)
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import StudentsPage from './pages/StudentsPage';
import TeachersPage from './pages/TeachersPage';
import ProductsPage from './pages/ProductsPage';
import BatchesPage from './pages/BatchesPage';
import EnrollmentsPage from './pages/EnrollmentsPage';
import AttendancePage from './pages/AttendancePage';
import InvoicesPage from './pages/InvoicesPage';
import PaymentsPage from './pages/PaymentsPage';
import HomePage from './pages/HomePage';
import PublicStorePage from './pages/PublicStorePage';

// Auth Components
import StudentLoginPage from './pages/StudentLoginPage';
import TeacherLoginPage from './pages/TeacherLoginPage';
import AdminRegisterPage from './pages/AdminRegisterPage';
import RegisterPage from './pages/RegisterPage';
import StudentDashboard from './pages/StudentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import ResetPasswordPage from './pages/ResetPasswordPage';

function AppRoutes() {
  const { isAuthenticated, isAdmin } = useAuth();
  const location = useLocation();

  const defaultDashboardRoute = isAdmin ? '/admin/dashboard' : '/dashboard';
  const legacyPaths = new Set(['/students','/teachers','/products','/batches','/enrollments','/attendance','/invoices','/payments']);
  const showNavbar = isAuthenticated && legacyPaths.has(location.pathname);

  const isHomePage = location.pathname === '/';
  const isStorePage = location.pathname === '/store';
  const isAuthPage = ['/login', '/register', '/student/register', '/admin/login', '/admin/register', '/reset-password', '/teacher/login'].includes(location.pathname);
  const isFullWidthPage = isHomePage || isStorePage || isAuthPage;

  return (
    <div className={`app ${isHomePage || isStorePage ? 'app-home' : ''}`}>
      {showNavbar && <Navbar />}
      <div className={`main-content ${isFullWidthPage ? 'main-content-home' : ''}`}>
        <Routes>
          <Route
            path="/"
            element={isAuthenticated ? <Navigate to={defaultDashboardRoute} replace /> : <HomePage />}
          />
          <Route path="/store" element={<PublicStorePage />} />

          {/* Public Auth Routes */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to={defaultDashboardRoute} replace /> : <StudentLoginPage />}
          />
          <Route
            path="/register"
            element={isAuthenticated ? <Navigate to={defaultDashboardRoute} replace /> : <RegisterPage />}
          />
          <Route
            path="/student/register"
            element={isAuthenticated ? <Navigate to={defaultDashboardRoute} replace /> : <RegisterPage />}
          />
          <Route
            path="/admin/login"
            element={isAuthenticated ? <Navigate to={defaultDashboardRoute} replace /> : <TeacherLoginPage />}
          />
          <Route
            path="/admin/register"
            element={isAuthenticated ? <Navigate to={defaultDashboardRoute} replace /> : <AdminRegisterPage />}
          />
          <Route
            path="/teacher/login"
            element={<Navigate to="/admin/login" replace />}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />

          {/* Protected User Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredRole="student">
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Protected Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredRole="admin">
                <TeacherDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/teacher/dashboard"
            element={
              <Navigate to="/admin/dashboard" replace />
            }
          />

          {/* Legacy Routes (with Navbar) */}
          <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><Dashboard /></ProtectedRoute>} />
          <Route path="/students" element={<ProtectedRoute requiredRole="admin"><StudentsPage /></ProtectedRoute>} />
          <Route path="/teachers" element={<ProtectedRoute requiredRole="admin"><TeachersPage /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute requiredRole="admin"><ProductsPage /></ProtectedRoute>} />
          <Route path="/batches" element={<ProtectedRoute requiredRole="admin"><BatchesPage /></ProtectedRoute>} />
          <Route path="/enrollments" element={<ProtectedRoute requiredRole="admin"><EnrollmentsPage /></ProtectedRoute>} />
          <Route path="/attendance" element={<ProtectedRoute requiredRole="admin"><AttendancePage /></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute requiredRole="admin"><InvoicesPage /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute requiredRole="admin"><PaymentsPage /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to={isAuthenticated ? defaultDashboardRoute : '/'} replace />} />
        </Routes>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
